import React from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { canonicalUrl } from '../../shared/utils/seo';

const SITE_URL = 'https://tenis.io';
const DEFAULT_DESCRIPTION =
  'Türkiye\'nin tenis platformu. Tenis liglerini, kortları, kulüpleri ve turnuvaları keşfet. Seviyene uygun lige katıl, tenis partneri bul ve maç yap.';
const DEFAULT_IMAGE = `${SITE_URL}/images/og-image.jpg`;

export default function BaseHead({
  pageTitle = 'Tenis oyna, partner bul, kort seç!',
  description = DEFAULT_DESCRIPTION,
  imageUrl = DEFAULT_IMAGE,
  noindex = false,
  type = 'website',
  shareUrl,
}: {
  pageTitle: string;
  description?: string;
  imageUrl?: string;
  noindex?: boolean;
  type?: 'website' | 'article';
  // Paylaşım önizlemesinin açacağı adres (ör. `?olay=` kart bağlantısı); canonical değişmez.
  shareUrl?: string;
}) {
  const router = useRouter();
  const normalizedTitle = pageTitle?.replace(/\s+/g, ' ').trim() || 'Tenis oyna, partner bul, kort seç!';
  const normalizedDescription = (description || DEFAULT_DESCRIPTION)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 160);
  const socialImage = imageUrl?.trim()
    ? imageUrl.startsWith('http')
      ? imageUrl
      : `${SITE_URL}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`
    : DEFAULT_IMAGE;
  const title = `${normalizedTitle} | tenis.io`;
  const canonical = canonicalUrl(router.asPath);

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={normalizedDescription} />
      <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large'} key="robots" />
      <link rel="canonical" href={canonical} key="canonical" />
      <link rel="alternate" hrefLang="tr-TR" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Open Graph */}
      <meta property="og:title" content={title} key="og:title" />
      <meta property="og:description" content={normalizedDescription} key="og:description" />
      <meta property="og:url" content={shareUrl || canonical} key="og:url" />
      <meta property="og:type" content={type} key="og:type" />
      <meta property="og:image" content={socialImage} key="og:image" />
      <meta property="og:image:secure_url" content={socialImage} key="og:image:secure_url" />
      {socialImage === DEFAULT_IMAGE && <meta property="og:image:width" content="1200" key="og:image:width" />}
      {socialImage === DEFAULT_IMAGE && <meta property="og:image:height" content="630" key="og:image:height" />}
      <meta property="og:image:alt" content={title} key="og:image:alt" />
      <meta property="og:site_name" content="tenis.io" key="og:site_name" />
      <meta property="og:locale" content="tr_TR" key="og:locale" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" key="twitter:card" />
      <meta name="twitter:title" content={title} key="twitter:title" />
      <meta name="twitter:description" content={normalizedDescription} key="twitter:description" />
      <meta name="twitter:image" content={socialImage} key="twitter:image" />
    </Head>
  );
}

BaseHead.defaultProps = {
  type: 'website',
  shareUrl: undefined,
  description: DEFAULT_DESCRIPTION,
  imageUrl: DEFAULT_IMAGE,
  noindex: false,
};
